// Signing in, for the whole app: a session for each pool (owners and staff), the API for each, the sign-in steps,
// and signing out. TUNE Analytics' own pages sign people in (pages/), calling Cognito's API from the browser.
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useMemo, useState, useSyncExternalStore, type ReactNode } from 'react';
import { forgetAnalysedLogs } from '../analyzer/forget';
import { forgetDrafts } from '../features/create/config/drafts';
import { makeApi, type Api } from '../api';
import type { Pool, PortalConfig } from '../config';
import { cognito, type AuthResult, type Cognito } from './cognito';
import { flows, type Flows } from './flows';
import { whoOf } from './session';
import { sessionKey, SessionStore, tabLocks, type Keep, type Locks } from './store';

export interface Session {
  pool: Pool;
  email: string;
  name: string | null;
  /** Kept in this browser, or only this tab. */
  keep: Keep;
}

interface Auth {
  config: PortalConfig;
  session(pool: Pool): Session | null;
  api(pool: Pool): Api;
  flows(pool: Pool): Flows;
  /** Signed in: the session is kept as the person chose (staff's only in the tab). */
  signedIn(pool: Pool, result: AuthResult, keep: Keep): void;
  /** Signs out here and in every tab, then goes to the front page (staff: their sign-in). */
  signOut(pool: Pool): Promise<void>;
  /** Whether the pool is signing out: its pages wait, rather than send people to sign in. */
  signingOut(pool: Pool): boolean;
  /** Signs every session of the pool's person out, on every device, then here. */
  signOutEverywhere(pool: Pool): Promise<void>;
  changePassword(pool: Pool, previous: string, proposed: string): Promise<void>;
  /** Keeps an owner's session in this browser, or only in this tab. Staff's stay in the tab. */
  keep(pool: Pool, keep: Keep): void;
  /** The access token now, for what calls the API on its own (uploads). */
  accessToken(pool: Pool): Promise<string | null>;
}

const AuthContext = createContext<Auth | null>(null);

function storageOf(kind: 'localStorage' | 'sessionStorage'): Storage | null {
  try {
    return window[kind];
  } catch {
    return null;
  }
}

function locks(): Locks {
  const native = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  // The lock's promise settles with the task's own result.
  return native
    ? { request: <T,>(name: string, task: () => Promise<T>) => native.request(name, task) as unknown as Promise<T> }
    : tabLocks();
}

export function AuthProvider({ config, children }: { config: PortalConfig; children: ReactNode }) {
  const queryClient = useQueryClient();
  const [leaving, setLeaving] = useState<Pool | null>(null);
  const { stores, api, cognitoApi } = useMemo(() => {
    const cognitoApi: Cognito = cognito(config.cognito);
    const channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('tune-auth') : null;
    const make = (pool: Pool) =>
      new SessionStore(pool, {
        // Staff stay signed in only in the tab they signed in with.
        local: pool === 'owners' ? storageOf('localStorage') : null,
        session: storageOf('sessionStorage'),
        locks: locks(),
        channel,
        now: Date.now,
        renew: async (refreshToken) =>
          (await cognitoApi.refresh(config[pool].clientId, refreshToken)).AuthenticationResult,
        revoke: (refreshToken) => cognitoApi.revoke(config[pool].clientId, refreshToken),
      });
    const stores = { owners: make('owners'), staff: make('staff') };
    const api = {
      owners: makeApi(
        (renew) => stores.owners.accessToken(renew),
        () => void stores.owners.signOut(),
      ),
      staff: makeApi(
        (renew) => stores.staff.accessToken(renew),
        () => void stores.staff.signOut(),
      ),
    };
    return { stores, api, cognitoApi };
  }, [config]);

  // Another tab signed in or out where this browser keeps sessions.
  useEffect(() => {
    const changed = (e: StorageEvent) => {
      for (const pool of ['owners', 'staff'] as const) {
        if (e.key === null || e.key === sessionKey(pool)) {
          stores[pool].reload();
        }
      }
    };
    window.addEventListener('storage', changed);
    return () => window.removeEventListener('storage', changed);
  }, [stores]);

  const owners = useSyncExternalStore(stores.owners.subscribe, stores.owners.getSnapshot);
  const staff = useSyncExternalStore(stores.staff.subscribe, stores.staff.getSnapshot);

  const auth = useMemo<Auth>(() => {
    const saved = { owners, staff };
    const leave = async (pool: Pool, everywhere: boolean) => {
      setLeaving(pool);
      const store = stores[pool];
      if (everywhere) {
        const token = await store.accessToken();
        if (token) {
          await cognitoApi.globalSignOut(token).catch(() => undefined);
        }
      }
      if (pool === 'owners') {
        forgetAnalysedLogs();
        await forgetDrafts().catch(() => undefined);
      }
      await store.signOut();
      queryClient.clear();
      // A fresh start: nothing of the session stays in the page.
      window.location.replace(pool === 'staff' ? '/admin/sign-in' : '/');
    };
    return {
      config,
      session: (pool) => {
        const s = saved[pool];
        return s ? { pool, ...whoOf(s.tokens), keep: s.keep } : null;
      },
      api: (pool) => api[pool],
      flows: (pool) => flows(cognitoApi, config[pool]),
      signedIn: (pool, result, keep) => {
        // A new person's data, never the last one's.
        queryClient.clear();
        stores[pool].signedIn(result, pool === 'staff' ? 'tab' : keep);
      },
      signOut: (pool) => leave(pool, false),
      signingOut: (pool) => leaving === pool,
      signOutEverywhere: (pool) => leave(pool, true),
      changePassword: async (pool, previous, proposed) => {
        const token = await stores[pool].accessToken();
        if (!token) {
          throw new Error('signed out');
        }
        await cognitoApi.changePassword(token, previous, proposed);
      },
      accessToken: (pool) => stores[pool].accessToken(),
      keep: (pool, keep) => stores[pool].keepIn(pool === 'staff' ? 'tab' : keep),
    };
  }, [owners, staff, stores, api, cognitoApi, config, leaving, queryClient]);

  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
}

export function useAuth(): Auth {
  const auth = useContext(AuthContext);
  if (!auth) {
    throw new Error('useAuth needs an AuthProvider');
  }
  return auth;
}

/** The pool's session, or null. */
export function useSession(pool: Pool): Session | null {
  return useAuth().session(pool);
}
